import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Grid, Skeleton } from '@mui/material';

import {
	CerebroText,
	CerebroPageContainer,
	CerebroHeading,
	CerebroPrimaryButton,
	CerebroBreadcrumb,
	CerebroDebtRealEstate,
	CerebroDebtEquipment,
	CerebroDebtOtherFinancingNeeds,
	CerebroDebtGrowthCapital,
	CerebroDebtWorkingCapital,
	CerebroDebtRefinance,
	isDebtEquipmentEmpty,
	isRefinanceEmpty,
	isWorkingCapitalEmpty,
	isGrowthCapitalEmpty,
	isOtherFinancingNeedsEmpty,
} from '../shared/components';
import {
	useDealRequestAllocationQuery,
	useDealRequestAllocationRealEstatesQuery,
	useDebtScheduleQuery,
	useNavigateToNextPage,
} from '../shared/hooks';
import { DealRequestAllocation, DealRequestAllocationRealEstate } from '../shared/models';

export const DebtCapital: React.FC = () => {
	const snackbar = useSnackbar();
	const navigate = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const allocationQuery = useDealRequestAllocationQuery(guid);
	const realEstatesQuery = useDealRequestAllocationRealEstatesQuery(guid);
	const debtScheduleQuery = useDebtScheduleQuery(guid);

	if (allocationQuery.isLoading || debtScheduleQuery.isLoading || realEstatesQuery.isLoading) {
		return <Skeleton height={400} />;
	}

	const sectionToDefaultDrawerOpen = getSectionToDefaultDrawerOpen(allocationQuery.data, realEstatesQuery.data ?? []);

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Deal &gt; Details</CerebroBreadcrumb>

			<CerebroHeading sx={{ marginBottom: 3 }}>
				Provide some details on the type of financing you need.
			</CerebroHeading>

			<CerebroText sx={{ marginBottom: 5.75 }}>
				This information helps us understand each piece of your total debt capital needs.
			</CerebroText>

			{allocationQuery.data?.purchaseWorkingCapital === true ? (
				<CerebroDebtWorkingCapital
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'workingCapital'}
				/>
			) : (
				<></>
			)}

			{allocationQuery.data?.financeExpensesOrGrowth === true ? (
				<CerebroDebtGrowthCapital
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'growthCapital'}
				/>
			) : (
				<></>
			)}

			{allocationQuery.data?.purchaseEquipment === true ? (
				<CerebroDebtEquipment
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'equipment'}
				/>
			) : (
				<></>
			)}

			{allocationQuery.data?.purchaseRealEstate === true ? (
				<CerebroDebtRealEstate
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'realEstate'}
				/>
			) : (
				<></>
			)}

			{allocationQuery.data?.refinanceExistingDebt === true ? (
				<CerebroDebtRefinance
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'refinance'}
				/>
			) : (
				<></>
			)}

			{allocationQuery.data?.otherFinancingNeeds === true ? (
				<CerebroDebtOtherFinancingNeeds
					allocation={allocationQuery.data}
					drawerOpenByDefault={sectionToDefaultDrawerOpen === 'other'}
				/>
			) : (
				<></>
			)}

			<Grid container justifyContent="space-between" sx={{ marginTop: 3, paddingBottom: 2 }}>
				<Grid item>
					<CerebroPrimaryButton
						type="button"
						onClick={() => {
							snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
							navigate();
						}}
					>
						Save &amp; Continue
					</CerebroPrimaryButton>
				</Grid>
			</Grid>
		</CerebroPageContainer>
	);
};

export interface DebtCapitalSectionProps {
	allocation: DealRequestAllocation;
	drawerOpenByDefault?: boolean;
}

function allSectionsAreEmpty(
	allocation: DealRequestAllocation,
	realEstates: DealRequestAllocationRealEstate[]
): boolean {
	return (
		(!allocation.purchaseEquipment || isDebtEquipmentEmpty(allocation)) &&
		(!allocation.refinanceExistingDebt || isRefinanceEmpty(allocation)) &&
		(!allocation.purchaseWorkingCapital || isWorkingCapitalEmpty(allocation)) &&
		(!allocation.financeExpensesOrGrowth || isGrowthCapitalEmpty(allocation)) &&
		(!allocation.otherFinancingNeeds || isOtherFinancingNeedsEmpty(allocation)) &&
		(!allocation.purchaseRealEstate || realEstates.length === 0)
	);
}

function getSectionToDefaultDrawerOpen(
	allocation: DealRequestAllocation | undefined,
	realEstates: DealRequestAllocationRealEstate[]
) {
	if (allocation === undefined) return '';

	if (allSectionsAreEmpty(allocation, realEstates)) {
		if (allocation.purchaseEquipment) return 'equipment';
		if (allocation.purchaseRealEstate) return 'realEstate';
		if (allocation.refinanceExistingDebt) return 'refinance';
		if (allocation.purchaseWorkingCapital) return 'workingCapital';
		if (allocation.financeExpensesOrGrowth) return 'growthCapital';
		if (allocation.otherFinancingNeeds) return 'other';
	}

	return '';
}
